import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Wine, Music, Users, Briefcase } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import BookingModal from '../../components/BookingModal';

const AfterWorkMonaco = () => {
  const { language } = useLanguage();
  const [showBookingModal, setShowBookingModal] = useState(false);

  const afterworkData = {
    id: '4917ffb0-d155-4dfd-8c08-adc5d19b6054',
    title: 'After-Work H&G – Monaco',
    title_en: 'H&G After-Work – Monaco',
    date: 'Jeudi 29 janvier 2026',
    location: 'Club 39 Monte-Carlo',
    price_member: 0,
    price_non_member: 550,
    total_seats: 30,
    seats_sold: 0
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
      {/* Hero Section */}
      <section style={{
        position: 'relative',
        height: '500px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0
        }}>
          <img
            src="https://customer-assets.emergentagent.com/job_4a16d148-a58f-4dee-a32d-adc2ae4e5015/artifacts/zi980d9m_39-Monte-Carlo-Restaurant.jpg"
            alt="Club 39 Monte-Carlo"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.4), rgba(255, 254, 242, 0.9))'
          }} />
        </div>

        <div className="container" style={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center'
        }}>
          <Link to="/member/afterwork" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '24px',
            color: 'var(--text-primary)',
            textDecoration: 'none',
            fontSize: '14px',
            padding: '8px 16px',
            background: 'rgba(255, 254, 242, 0.9)',
            border: '1px solid var(--border-light)'
          }}>
            <ArrowLeft size={16} />
            {language === 'fr' ? 'Retour aux after-work' : 'Back to after-work'}
          </Link>
          
          <div style={{
            display: 'inline-block',
            padding: '6px 12px',
            background: '#8b4f6d',
            color: 'var(--bg-primary)',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            marginBottom: '16px'
          }}>
            {language === 'fr' ? 'Édition Prestige' : 'Prestige Edition'}
          </div>

          <h1 className="hero-large" style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
            After-Work H&G – Monaco
          </h1>
          
          <div style={{
            display: 'flex',
            gap: '24px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={20} />
              <span className="body-large">{language === 'fr' ? 'Jeudi 29 janvier 2026' : 'Thursday, January 29, 2026'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MapPin size={20} />
              <span className="body-large">Club 39 Monte-Carlo</span>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <h2 className="hero-medium" style={{ marginBottom: '24px', textAlign: 'center' }}>
            Club 39 Monte-Carlo
          </h2>
          <p className="body-large" style={{
            lineHeight: 1.8,
            textAlign: 'center',
            color: 'var(--text-secondary)'
          }}>
            {language === 'fr'
              ? "Un cercle d'invités sélectionnés au Club 39 Monte-Carlo, sur l'Avenue Princesse Grace. Le networking haut niveau à la monégasque."
              : 'A select circle of guests at Club 39 Monte-Carlo, on Avenue Princesse Grace. High-level networking, Monaco style.'}
          </p>
        </div>
      </section>

      {/* Champagne, Caviar & Cigares */}
      <section className="section-padding">
        <div className="container" style={{ maxWidth: '1000px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '24px',
            justifyContent: 'center'
          }}>
            <Wine size={32} color="#8b4f6d" />
            <h2 className="hero-medium">
              Champagne, Caviar & Cigares
            </h2>
          </div>
          
          <p className="body-regular" style={{ 
            lineHeight: 1.8, 
            color: 'var(--text-secondary)',
            textAlign: 'center',
            marginBottom: '32px'
          }}>
            {language === 'fr'
              ? "Une expérience sensorielle d'exception :"
              : 'An exceptional sensory experience:'}
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '24px'
          }}>
            <div style={{
              padding: '28px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-light)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '40px', marginBottom: '16px' }}>🥂</div>
              <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                Champagne
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                {language === 'fr' ? 'Champagne millésimé servi en continu' : 'Vintage champagne served continuously'}
              </p>
            </div>

            <div style={{
              padding: '28px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-light)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '40px', marginBottom: '16px' }}>🦪</div>
              <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                Caviar
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                {language === 'fr' ? 'Dégustation de caviar en accords raffinés' : 'Caviar tasting in refined pairings'}
              </p>
            </div>

            <div style={{
              padding: '28px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-light)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '40px', marginBottom: '16px' }}>🚬</div>
              <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                Cigares
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                {language === 'fr' ? 'Espace cigare premium avec sélection haut de gamme' : 'Premium cigar area with high-end selection'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Jazz Live */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '24px',
            justifyContent: 'center'
          }}>
            <Music size={32} color="#8b4f6d" />
            <h2 className="hero-medium">
              {language === 'fr' ? 'Ambiance Musicale : Jazz Live & Trompette' : 'Musical Atmosphere: Live Jazz & Trumpet'}
            </h2>
          </div>
          
          <p className="body-regular" style={{ 
            lineHeight: 1.8, 
            color: 'var(--text-secondary)',
            textAlign: 'center',
            marginBottom: '24px'
          }}>
            {language === 'fr'
              ? "Pour Monaco, on reste dans la signature H&G :"
              : 'For Monaco, we stay with the H&G signature:'}
          </p>

          <div style={{
            display: 'grid',
            gap: '16px',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            {[
              language === 'fr' ? 'Trompettiste live' : 'Live trumpeter',
              language === 'fr' ? 'Standards jazz revisités' : 'Revisited jazz standards',
              language === 'fr' ? 'Atmosphère feutrée, élégante, immersive' : 'Intimate, elegant, immersive atmosphere',
              language === 'fr' ? 'Parfait pour networking haut niveau' : 'Perfect for high-level networking'
            ].map((item, index) => (
              <div key={index} style={{
                padding: '16px 24px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-light)',
                display: 'flex',
                gap: '16px',
                alignItems: 'center'
              }}>
                <span style={{ color: '#8b4f6d', fontWeight: 'bold', fontSize: '20px' }}>♪</span>
                <span className="body-regular">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rencontres & Réseau */}
      <section className="section-padding">
        <div className="container" style={{ maxWidth: '1000px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '24px',
            justifyContent: 'center'
          }}>
            <Briefcase size={32} color="#8b4f6d" />
            <h2 className="hero-medium">
              {language === 'fr' ? 'Rencontres & Réseau' : 'Meetings & Network'}
            </h2>
          </div>
          
          <p className="body-regular" style={{ 
            lineHeight: 1.8, 
            color: 'var(--text-secondary)',
            textAlign: 'center'
          }}>
            {language === 'fr'
              ? "Un cercle d'invités sélectionnés : dirigeants, investisseurs, personnalités du monde du luxe, de l'art et de l'aviation. Un moment privilégié pour créer des connexions stratégiques."
              : 'A select circle of guests: executives, investors, personalities from the world of luxury, art and aviation. A privileged moment to create strategic connections.'}
          </p>
        </div>
      </section>

      {/* Informations Pratiques */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <h2 className="hero-medium" style={{ marginBottom: '48px', textAlign: 'center' }}>
            {language === 'fr' ? 'Informations Pratiques' : 'Practical Information'}
          </h2>
          
          <div style={{
            padding: '32px',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-light)'
          }}>
            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <p className="body-regular" style={{ fontWeight: 600, marginBottom: '8px' }}>
                  {language === 'fr' ? 'Date' : 'Date'}
                </p>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {language === 'fr' ? 'Jeudi 29 janvier 2026' : 'Thursday, January 29, 2026'}
                </p>
              </div>
              <div>
                <p className="body-regular" style={{ fontWeight: 600, marginBottom: '8px' }}>
                  {language === 'fr' ? 'Lieu' : 'Location'}
                </p>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  Club 39 Monte-Carlo – Avenue Princesse Grace
                </p>
              </div>
              <div>
                <p className="body-regular" style={{ fontWeight: 600, marginBottom: '8px' }}>
                  Format
                </p>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {language === 'fr' ? 'After-Work Élégance & Prestige' : 'After-Work Elegance & Prestige'}
                </p>
              </div>
              <div>
                <p className="body-regular" style={{ fontWeight: 600, marginBottom: '8px' }}>
                  Dress code
                </p>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {language === 'fr' ? 'Élégance contemporaine' : 'Contemporary elegance'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding-small">
        <div className="container" style={{ maxWidth: '600px', textAlign: 'center' }}>
          <p className="body-large" style={{ marginBottom: '24px', lineHeight: 1.7 }}>
            {language === 'fr'
              ? 'Réservez votre place pour cet after-work exclusif'
              : 'Book your place for this exclusive after-work'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            <button onClick={() => setShowBookingModal(true)} className="btn-primary">
              {language === 'fr' ? 'RÉSERVER' : 'BOOK NOW'}
            </button>
            <Link to="/member/afterwork" className="btn-secondary">
              {language === 'fr' ? 'Retour aux After-Works' : 'Back to After-Works'}
            </Link>
          </div>
        </div>
      </section>

      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        item={afterworkData}
        bookingType="afterwork"
        onSuccess={() => setShowBookingModal(false)}
      />
    </div>
  );
};

export default AfterWorkMonaco;